import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import {
  MultiSelect,
  TextInput,
  Accordion,
  Divider,
  Button,
  Popover,
} from '@mantine/core'
import {
  useState,
  useEffect,
  useMemo,
  type ChangeEvent,
  memo,
  useRef,
  useCallback,
} from 'react'

import { useTranslation } from '@flecto/translations'
import { type UNSAFE_any } from '@flecto/types'

import { type FlectoAnalyticsEvents } from '../../../flecto-analytics/src/events'
import { detectArrayElementDifferences } from '../../lib/utils'
import { Badge } from '../Badge/Badge'
import { Icon } from '../Icon/Icon'
import { AccordionControlHeader } from './components/accordion/AccordionControlHeader'
import { CheckboxGroup } from './components/checkbox/CheckboxGroup'
import { getCheckboxGroupOptions } from './components/checkbox/getCheckboxGroupOptions'
import { DateTimeRangeFilterWrapper } from './components/date-range/wrapper/DateTimeRangeFilterWrapper'
import { DateTimeFilterWrapper } from './components/date-time/wrapper/DateTimeFilterWrapper'
import { FiltersFooter } from './components/footer/FiltersFooter'
import { createMultiSelectProps } from './components/multi-select/createMultiSelectProps'
import {
  MULTISELECT_QUERY_FIELD_TYPES,
  CHECKBOX_GROUP_FIELDS,
  type FilterField,
  type FilterValue,
} from './components/multi-select/filterTypeConfig'
import { createMultiSelectQueryProps } from './components/multi-select/query/createMultiSelectQueryProps'
import { useCloseFilterOnClickOutside } from './hooks/useCloseFilterOnClickOutside'
import { useFilterStore } from './store/useFilterStore'
import styles from './styles.module.css'
import { flattenFilters } from './util/flattenFilters'

export type FiltersProps = {
  id: string
  isLoading?: boolean
  fields: FilterField[]
  onFilter: (filters: FilterValue[]) => void
  onTrackEvent?: (
    eventName: keyof FlectoAnalyticsEvents,
    data: Record<string, UNSAFE_any>
  ) => void
}

export const Filters = memo(
  ({
    id,
    fields,
    onFilter,
    isLoading = false,
    onTrackEvent,
  }: Readonly<FiltersProps>) => {
    const { t } = useTranslation('shared', { keyPrefix: 'filters' })
    const { appliedFilters, setAppliedFilters, clearAppliedFilters } =
      useFilterStore(id)
    const [selectedFilters, setSelectedFilters] =
      useState<FilterValue[]>(appliedFilters)
    const [accordionItemHovered, setAccordionItemHovered] = useState<
      string | null
    >(null)
    const [openItem, setOpenItem] = useState<string | null>(null)
    const [opened, setOpened] = useState(false)
    const [isDateRangeInvalid, setIsDateRangeInvalid] = useState(false)
    const [clearedFilters, setClearedFilters] = useState<FilterValue[]>([])
    const popoverRef = useRef<HTMLDivElement | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)

    useEffect(() => {
      onFilter(appliedFilters)
    }, [appliedFilters])

    const handleFilterChange = (key: string, value: string | string[]) => {
      setSelectedFilters((prev: FilterValue[]) => {
        const existingFilter = prev.find((f) => f.key === key)
        if (existingFilter) {
          if (Array.isArray(value) && value.length === 0) {
            return prev.filter((f) => f.key !== key)
          }
          return prev.map((f) => (f.key === key ? { key, value } : f))
        } else {
          return [...prev, { key, value }]
        }
      })
    }

    const handleClearFilter = (key: string) => {
      const filterToClear = selectedFilters.find((f) => f.key === key)
      setSelectedFilters(selectedFilters.filter((f) => f.key !== key))

      if (filterToClear) {
        setClearedFilters((prev) => [...prev, filterToClear])
      }
    }

    const handleClearAllFilters = () => {
      setOpened(false)
      clearAppliedFilters()
      setSelectedFilters([])
      setClearedFilters([])

      if (onTrackEvent) {
        onTrackEvent('FILTERS__CLEAR_ALL', {
          table: id,
          data: {},
        })
      }
    }

    const isValidFilter = (filter: FilterValue) => {
      if (filter.key === 'date-range') {
        if (isDateRangeInvalid) {
          return false
        }
      }
      return true
    }

    const handleApplyFilters = () => {
      const validSelectedFilters = selectedFilters.filter(isValidFilter)
      setAppliedFilters(validSelectedFilters)
      onFilter(validSelectedFilters)
      setSelectedFilters(validSelectedFilters)
      setOpened(false)

      if (onTrackEvent) {
        onTrackEvent('FILTERS__APPLY', {
          table: id,
          data: {
            apply: flattenFilters(validSelectedFilters),
            clear: flattenFilters(clearedFilters),
          },
        })
        setClearedFilters([])
      }
    }

    const hasUnappliedChanges = useMemo(
      () =>
        detectArrayElementDifferences(
          selectedFilters.filter(isValidFilter),
          appliedFilters
        ),
      [selectedFilters, appliedFilters, isDateRangeInvalid]
    )

    const isClearAllEnabled =
      selectedFilters.length > 0 || appliedFilters.length > 0

    const handlePopoverOpen = () => {
      setOpened(true)

      if (onTrackEvent) {
        onTrackEvent('FILTERS__NEW', {
          table: id,
          data: flattenFilters(appliedFilters),
        })
      }
    }

    const handlePopoverClose = useCallback(() => {
      setOpened(false)
      setSelectedFilters([...appliedFilters])
    }, [appliedFilters])

    useCloseFilterOnClickOutside(popoverRef, buttonRef, handlePopoverClose)

    return (
      <Popover
        shadow='md'
        opened={opened}
        position='bottom-start'
        onClose={handlePopoverClose}
        data-testid='popover'
      >
        <Popover.Target>
          <Button
            color='gray'
            ref={buttonRef}
            variant='outline'
            loading={isLoading}
            classNames={{ root: styles.filterButton }}
            onClick={opened ? handlePopoverClose : handlePopoverOpen}
            rightSection={
              appliedFilters.length > 0 && (
                <Badge
                  className={styles.filterBadge}
                  classNames={{ label: styles.filterBadgeLabel }}
                  variant='primary'
                  color='primary'
                  size='md'
                  data-testid='filter-button-badge'
                >
                  {appliedFilters.length}
                </Badge>
              )
            }
            data-testid='filter-button'
          >
            {t('filters')}
          </Button>
        </Popover.Target>
        <Popover.Dropdown
          w={300}
          ref={popoverRef}
          className={styles.filterPopoverDropdown}
          data-testid='popover-dropdown'
        >
          <Accordion
            variant='contained'
            classNames={{
              root: styles.filterAccordionRoot,
              item: styles.filterAccordionItem,
              label: styles.filterAccordionItemLabel,
              content: styles.filterAccordionContent,
            }}
            onChange={(key) => setOpenItem(key)}
            data-testid='accordion'
          >
            {fields.map((field) => (
              <Accordion.Item
                key={field.key}
                value={field.key}
                onMouseEnter={() => setAccordionItemHovered(field.key)}
                onMouseLeave={() => setAccordionItemHovered(null)}
                data-testid={`accordion-item-${field.key}`}
              >
                <Accordion.Control
                  data-testid={`accordion-control-${field.key}`}
                >
                  <AccordionControlHeader
                    id={id}
                    field={field}
                    isOpen={openItem === field.key}
                    selectedFilters={selectedFilters}
                    handleClearFilter={handleClearFilter}
                    accordionItemHovered={accordionItemHovered === field.key}
                  />
                </Accordion.Control>
                <Accordion.Panel data-testid={`accordion-panel-${field.key}`}>
                  {field.type === 'text' && (
                    <TextInput
                      placeholder={`Search ${field.label}`}
                      value={
                        (selectedFilters.find((f) => f.key === field.key)
                          ?.value as string) || ''
                      }
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        handleFilterChange(field.key, event.currentTarget.value)
                      }
                      leftSectionPointerEvents='none'
                      leftSection={<Icon icon={faSearch} size='sm' />}
                      data-testid={`text-input-${field.key}`}
                    />
                  )}

                  {field.type === 'multiselect' && (
                    <MultiSelect
                      {...createMultiSelectProps(
                        field,
                        selectedFilters,
                        handleFilterChange
                      )}
                      data-testid={`multiselect-${field.key}`}
                    />
                  )}

                  {MULTISELECT_QUERY_FIELD_TYPES.includes(field.type) && (
                    <MultiSelect
                      {...createMultiSelectQueryProps(
                        field,
                        selectedFilters,
                        handleFilterChange,
                        id
                      )}
                      data-testid={`multiselect-query-${field.key}`}
                    />
                  )}

                  {CHECKBOX_GROUP_FIELDS.includes(field.type) && (
                    <CheckboxGroup
                      {...getCheckboxGroupOptions(
                        field,
                        selectedFilters,
                        handleFilterChange
                      )}
                    />
                  )}

                  {field.type === 'date-range' && (
                    <DateTimeRangeFilterWrapper
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                      onInvalidRangeChange={setIsDateRangeInvalid}
                    />
                  )}

                  {field.type === 'date-time' && (
                    <DateTimeFilterWrapper
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                      field={field}
                    />
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
          <Divider />
          <FiltersFooter
            handleClearAllFilters={handleClearAllFilters}
            hasUnappliedChanges={hasUnappliedChanges}
            handleApplyFilters={handleApplyFilters}
            isClearAllEnabled={isClearAllEnabled}
            data-testid='filters-footer'
            isLoading={isLoading}
          />
        </Popover.Dropdown>
      </Popover>
    )
  }
)

Filters.displayName = 'Filters'
